// WebSocket 消息类型
export interface WSMessage {
  id: string;
  type: 'notification' | 'chat' | 'system' | 'task' | 'project';
  title: string;
  content: string;
  senderId?: string;
  senderName?: string;
  recipientId?: string;
  projectId?: string;
  taskId?: string;
  createdAt: string;
  read: boolean;
}

// WebSocket 连接状态
export type WSConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

// WebSocket 事件监听器类型
type WSListener = (message: WSMessage) => void;

class WebSocketService {
  private socket: WebSocket | null = null;
  private url: string;
  private state: WSConnectionState = 'disconnected';
  private listeners: Map<string, WSListener[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private pingInterval: NodeJS.Timeout | null = null;
  private pingTimeout: NodeJS.Timeout | null = null;

  constructor() {
    this.url = import.meta.env?.VITE_WS_URL || 'ws://localhost:3002/ws/notifications';
  }

  // 连接 WebSocket
  connect(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.state === 'connected' || this.state === 'connecting') {
        resolve();
        return;
      }

      this.state = 'connecting';
      let isResolved = false;

      const resolveOnce = () => {
        if (!isResolved) {
          isResolved = true;
          resolve();
        }
      };

      const rejectOnce = (err: any) => {
        if (!isResolved) {
          isResolved = true;
          reject(err);
        }
      };

      try {
        this.socket = new WebSocket(`${this.url}?token=${token}`);

        this.socket.onopen = () => {
          console.log('WebSocket 连接成功');
          this.state = 'connected';
          this.reconnectAttempts = 0;
          this.startPing();
          resolveOnce();
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // 处理 pong 响应，清除超时计时器
            if (data.type === 'pong') {
              if (this.pingTimeout) {
                clearTimeout(this.pingTimeout);
                this.pingTimeout = null;
              }
              return;
            }

            const message: WSMessage = data;
            this.handleMessage(message);
          } catch (error) {
            console.error('WebSocket 消息解析失败:', error);
          }
        };

        this.socket.onerror = (error) => {
          console.error('WebSocket 错误:', error);
          this.state = 'error';
          // 连接尚未成功时 reject，否则由重连逻辑处理
          rejectOnce(error);
          this.attemptReconnect(token);
        };

        this.socket.onclose = () => {
          console.log('WebSocket 连接关闭');
          this.state = 'disconnected';
          this.stopPing();
          this.attemptReconnect(token);
        };
      } catch (error) {
        console.error('WebSocket 连接失败:', error);
        this.state = 'error';
        this.attemptReconnect(token);
        rejectOnce(error);
      }
    });
  }

  // 断开 WebSocket 连接
  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.state = 'disconnected';
    this.stopPing();
    this.listeners.clear();
  }

  // 发送消息
  send(message: Partial<WSMessage>): void {
    if (this.state === 'connected' && this.socket) {
      try {
        this.socket.send(JSON.stringify(message));
      } catch (error) {
        console.error('WebSocket 发送消息失败:', error);
      }
    } else {
      console.error('WebSocket 未连接，无法发送消息');
    }
  }

  // 订阅消息
  subscribe(type: string, listener: WSListener): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type)?.push(listener);
  }

  // 取消订阅
  unsubscribe(type: string, listener: WSListener): void {
    const listeners = this.listeners.get(type);
    if (listeners) {
      this.listeners.set(type, listeners.filter(l => l !== listener));
    }
  }

  // 处理接收到的消息
  private handleMessage(message: WSMessage): void {
    // 通知所有订阅该类型的监听器
    const typeListeners = this.listeners.get(message.type);
    if (typeListeners) {
      typeListeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          console.error('WebSocket 监听器执行失败:', error);
        }
      });
    }

    // 通知所有订阅 'all' 的监听器
    const allListeners = this.listeners.get('all');
    if (allListeners) {
      allListeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          console.error('WebSocket 监听器执行失败:', error);
        }
      });
    }
  }

  // 尝试重连
  private attemptReconnect(token: string): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      console.log(`WebSocket 尝试重连... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect(token).catch(error => {
          console.error('WebSocket 重连失败:', error);
        });
      }, delay);
    } else {
      console.error('WebSocket 重连失败，已达到最大重试次数');
    }
  }

  // 开始心跳检测
  private startPing(): void {
    this.stopPing();

    // 每 15 秒发送一次 ping（更频繁，减少超时风险）
    this.pingInterval = setInterval(() => {
      if (this.state === 'connected' && this.socket) {
        try {
          this.socket.send(JSON.stringify({ type: 'ping' }));

          // 设置超时，如果 5 秒内没有收到 pong，断开连接
          this.pingTimeout = setTimeout(() => {
            console.warn('WebSocket 心跳超时，尝试重连');
            this.socket?.close();
          }, 5000);
        } catch (error) {
          console.error('WebSocket 发送 ping 失败:', error);
        }
      }
    }, 15000);
  }

  // 停止心跳检测
  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
  }

  // 获取连接状态
  getState(): WSConnectionState {
    return this.state;
  }

  // 检查是否连接
  isConnected(): boolean {
    return this.state === 'connected';
  }
}

// 导出单例实例
export const wsService = new WebSocketService();